import { useState } from "react";
import { FileText, Plus, RefreshCw, Loader2, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useVehicleDocuments, VEHICLE_DOC_TYPES, getRequiredDocs } from "@/hooks/useVehicles";
import type { VehicleDocument } from "@/hooks/useVehicles";
import type { Database } from "@/integrations/supabase/types";
import { UnifiedDocumentLinkDialog } from "@/components/shared/UnifiedDocumentLinkDialog";
import { AddAndLinkVehicleDocumentDialog } from "./AddAndLinkVehicleDocumentDialog";
import { supabase } from "@/integrations/supabase/client";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import DocumentChecklistCard, { DocumentStatus } from "@/components/shared/DocumentChecklistCard";

type VehicleCategory = Database["public"]["Enums"]["vehicle_category"];

interface VehicleChecklistProps {
  vehicleId: string;
  category: VehicleCategory;
  showAddButton?: boolean;
}

// Map vehicle document status to unified DocumentStatus
const mapStatus = (vehicleStatus: string): DocumentStatus => {
  switch (vehicleStatus) {
    case "valid": return "valid";
    case "expiring_soon": return "expiring";
    case "expired": return "expired";
    case "missing":
    case "pending_verification":
    default: return "missing";
  }
};

const getStatusLabel = (vehicleStatus: string): string => {
  switch (vehicleStatus) {
    case "valid": return "Valide";
    case "expiring_soon": return "Expire bientôt";
    case "expired": return "Expiré";
    case "pending_verification": return "En attente";
    case "missing":
    default: return "Manquant";
  }
};

export function VehicleChecklist({ vehicleId, category, showAddButton = false }: VehicleChecklistProps) {
  const { documents, isLoading, unlinkDocument, initializeDocuments } = useVehicleDocuments(vehicleId);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<VehicleDocument | null>(null);
  const { openDocument } = useDocumentPreview();
  
  const requiredDocTypes = getRequiredDocs(category);

  const handleLinkClick = (doc: VehicleDocument) => {
    setSelectedDoc(doc);
    setLinkDialogOpen(true);
  };

  const handleLinkSuccess = () => {
    setLinkDialogOpen(false);
    setSelectedDoc(null);
  };

  const handleViewDocument = async (documentId: string) => {
    try {
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("id", documentId)
        .single();

      if (error) throw error;
      if (data) {
        openDocument(data);
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const handleUnlinkDocument = (doc: VehicleDocument) => {
    unlinkDocument.mutate(doc.id);
  };

  const handleInitializeDocuments = () => {
    initializeDocuments.mutate(category);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  // Group documents: mandatory first, then others
  const mandatoryDocs = documents.filter(d => requiredDocTypes.includes(d.doc_type));
  const otherDocs = documents.filter(d => !requiredDocTypes.includes(d.doc_type));

  const renderDocCard = (doc: VehicleDocument, isMandatory: boolean) => {
    const docConfig = VEHICLE_DOC_TYPES[doc.doc_type];
    
    return (
      <DocumentChecklistCard
        key={doc.id}
        typeKey={doc.doc_type}
        label={doc.label || docConfig?.label || doc.doc_type}
        description={`Document ${isMandatory ? "obligatoire" : "optionnel"} du véhicule`}
        icon={Car}
        required={isMandatory}
        status={mapStatus(doc.status)}
        statusLabel={getStatusLabel(doc.status)}
        linkedDocument={doc.linked_document ? {
          id: doc.linked_document.id,
          title: doc.linked_document.title,
          file_name: doc.linked_document.file_name,
          file_path: doc.linked_document.file_path,
        } : null}
        expiresAt={doc.expires_at}
        onLink={() => handleLinkClick(doc)}
        onView={handleViewDocument}
        onUnlink={() => handleUnlinkDocument(doc)}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with add button */}
      {(showAddButton || documents.length > 0) && (
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Documents du véhicule
          </h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setAddDialogOpen(true)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Ajouter
          </Button>
        </div>
      )}

      {/* Mandatory documents */}
      {mandatoryDocs.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Documents obligatoires
          </h4>
          <div className="space-y-3">
            {mandatoryDocs.map(doc => renderDocCard(doc, true))}
          </div>
        </div>
      )}

      {/* Other documents */}
      {otherDocs.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Autres documents
          </h4>
          <div className="space-y-3">
            {otherDocs.map(doc => renderDocCard(doc, false))}
          </div>
        </div>
      )}

      {/* Empty state with CTA */}
      {documents.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="mb-4">Aucun document configuré pour ce véhicule.</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={handleInitializeDocuments}
              disabled={initializeDocuments.isPending}
            >
              {initializeDocuments.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Initialiser les documents obligatoires
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setAddDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un document
            </Button>
          </div>
        </div>
      )}

      {/* Link dialog */}
      {selectedDoc && (
        <UnifiedDocumentLinkDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          entityType="vehicle"
          entityId={vehicleId}
          docConfig={{
            key: selectedDoc.doc_type,
            label: VEHICLE_DOC_TYPES[selectedDoc.doc_type]?.label || selectedDoc.doc_type,
          }}
          linkTableName="vehicle_documents"
          onSuccess={handleLinkSuccess}
        />
      )}

      {/* Add document dialog */}
      <AddAndLinkVehicleDocumentDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        vehicleId={vehicleId}
        existingDocTypes={documents.map(d => d.doc_type)}
      />
    </div>
  );
}
